import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useReducedMotion } from "framer-motion";
import {
  FLIP_KEYFRAMES,
  flightKeyframes,
  flightSpan,
  flightTiming,
  measureFlight,
  opacityEasing,
  opacityKeyframes,
  type CardFlight,
  type FlightPath,
} from "../../lib/card-flight";
import { CardBackMark } from "./CardBackMark";

/** Part de la largeur d'une carte que sa valeur occupe, dos et faces confondus. */
const FACE_VALUE_RATIO = 0.46;

const valueLabel = (value: number) => (value < 0 ? `−${Math.abs(value)}` : String(value));

const valueTone = (value: number | null) => {
  if (value === null) return "hidden";
  if (value < 0) return "negative";
  if (value === 0) return "zero";
  if (value <= 4) return "low";
  if (value <= 8) return "mid";
  return "high";
};

interface CardFlightViewProps {
  flight: CardFlight;
  onComplete: (flightId: string) => void;
}

const CardFlightView = ({ flight, onComplete }: CardFlightViewProps) => {
  // `null` tant que la préférence n'a pas été lue : pour un trajet, c'est un
  // « non » — les images clés en ont besoin comme booléen ferme.
  const reducedMotion = useReducedMotion() === true;
  const [path, setPath] = useState<FlightPath | null>(null);
  const card = useRef<HTMLDivElement>(null);
  const flipper = useRef<HTMLSpanElement>(null);
  const faceUp = flight.value !== null;
  const flips = faceUp && flight.startsHidden && !reducedMotion;

  useLayoutEffect(() => {
    const measured = measureFlight(flight);
    if (measured) {
      setPath(measured);
      return;
    }
    // Repère introuvable — plateau démonté, adversaire parti. Le vol n'a pas de
    // trajet, mais il ne doit pas pour autant bloquer la file d'attente.
    const timeout = window.setTimeout(() => onComplete(flight.id), 0);
    return () => window.clearTimeout(timeout);
  }, [flight, onComplete]);

  // Filet de sécurité : une animation qui ne rend jamais la main — onglet mis en
  // veille, moteur qui saute une image — laisserait la file bloquée, donc des
  // cases masquées et un tableau des scores qui n'arrive pas. Il vaut aussi
  // contre `finished`, qui ne se résout pas sur un onglet endormi. `completeFlight`
  // ignore un vol déjà retiré : le doublon avec la fin normale est sans effet.
  useEffect(() => {
    const guard = window.setTimeout(
      () => onComplete(flight.id),
      (flightSpan(flight) + 0.4) * 1000,
    );
    return () => window.clearTimeout(guard);
  }, [flight, onComplete]);

  /**
   * Le trajet ne peut être joué qu'une fois la mesure faite : c'est elle qui
   * monte l'élément, et cet effet ne s'arme donc qu'au rendu suivant.
   */
  useLayoutEffect(() => {
    const element = card.current;
    if (!path || !element) return;
    if (typeof element.animate !== "function") {
      // Aucun moteur capable de charger ce paquet n'en est là. Si cela arrivait,
      // la carte doit rester visible : le filet de sécurité refermera le vol.
      element.style.opacity = "1";
      return;
    }

    const timing = flightTiming(flight, reducedMotion);
    const running = [
      element.animate(flightKeyframes(path, reducedMotion), timing),
      element.animate(opacityKeyframes(reducedMotion), {
        ...timing,
        easing: opacityEasing(reducedMotion),
      }),
    ];
    if (flips && flipper.current) running.push(flipper.current.animate(FLIP_KEYFRAMES, timing));

    let unmounted = false;
    running[0].finished
      .then(() => {
        if (!unmounted) onComplete(flight.id);
      })
      // Le démontage annule l'animation, ce qui rejette la promesse : c'est une
      // fin attendue, pas une erreur.
      .catch(() => {});

    return () => {
      unmounted = true;
      running.forEach((animation) => animation.cancel());
    };
  }, [flight, flips, onComplete, path, reducedMotion]);

  if (!path) return null;

  return (
    <div
      className={`card-flight card-flight--${flight.tone} card-flight--${flight.role}`}
      data-flight-id={flight.id}
      data-player-id={flight.playerId}
      // L'état de départ est déjà en style : l'élément est peint au moins une
      // fois avant que l'animation ne prenne la main, et sans lui la carte
      // clignoterait en haut à gauche, à taille pleine.
      style={{
        width: path.width,
        height: path.height,
        // Le chiffre suit la carte, dont la largeur sort d'une mesure et non
        // d'un palier : la proportion est celle qu'une carte de pile garde
        // entre sa valeur et son cadre.
        fontSize: path.width * FACE_VALUE_RATIO,
        opacity: 0,
        transform: (flightKeyframes(path, reducedMotion)[0].transform as string) ?? undefined,
      }}
      ref={card}
    >
      <i className="card-flight__glow" />
      <span
        className="card-flight__flip"
        style={{ transform: `rotateY(${!flips && faceUp ? 180 : 0}deg)` }}
        ref={flipper}
      >
        <span className="card-flight__face card-flight__back pile-card pile-card--back">
          <CardBackMark />
        </span>
        <span
          className={`card-flight__face card-flight__front pile-card pile-card--${valueTone(flight.value)}`}
        >
          {flight.value !== null && <strong>{valueLabel(flight.value)}</strong>}
        </span>
      </span>
    </div>
  );
};

interface CardFlightLayerProps {
  flights: CardFlight[];
  onComplete: (flightId: string) => void;
}

/**
 * Toutes les cartes en mouvement partagent une seule couche, posée au-dessus du
 * plateau : elles se croisent sans jamais passer sous un panneau.
 */
export const CardFlightLayer = ({ flights, onComplete }: CardFlightLayerProps) => {
  if (flights.length === 0) return null;

  return createPortal(
    <div className="card-flight-layer" aria-hidden="true">
      {flights.map((flight) => (
        <CardFlightView key={flight.id} flight={flight} onComplete={onComplete} />
      ))}
    </div>,
    document.body,
  );
};
